import moment from 'moment'

import {
  accumulationTypes,
  shareholderTypes,
  antiDilutionTransferTypes,
  antiDilutionCompensation,
  antiDilutionDuration,
  antiDilutionTypes,
} from 'utils/enums'
import { date } from 'utils/format'
import { userToInput } from './inputFunctions'

/* ******************** INPUTS for add Remote ******************** */

export function newEmployeeLinkSetup(
  translate,
  employeeName = 'NAME',
  defaultExitAmount = 100000000,
  defaultExitDate = date(new Date()).add(4, 'year'),
) {
  return {
    defaultExitAmount,
    defaultExitDate,
    text1: translate('employeeDashboard.box.intro.description'),
    text2: translate('employeeDashboard.box.custom.description'),
    header1: translate('employeeDashboard.box.intro.title', { name: employeeName }),
    header2: translate('employeeDashboard.box.custom.title'),
  }
}

export function newEsopSetup(lastPostMoneyValuation, translate) {
  return {
    exitAmount: lastPostMoneyValuation,
    exitDate: new Date(),
    defaultCompensationPeriodInMonths: 48,
    bonusCompensationLevels: [
      {
        name: translate('esopSetup.bonusCompensationLevels.normalEmployee'),
        percentFromSalary: 0.05,
        __typename: 'BonusCompensationLevel',
      },
      {
        name: translate('esopSetup.bonusCompensationLevels.keyContributor'),
        percentFromSalary: 0.1,
        __typename: 'BonusCompensationLevel',
      },
      {
        name: translate('esopSetup.bonusCompensationLevels.lead'),
        percentFromSalary: 0.2,
        __typename: 'BonusCompensationLevel',
      },
      {
        name: translate('esopSetup.bonusCompensationLevels.headOfVP'),
        percentFromSalary: 0.5,
        __typename: 'BonusCompensationLevel',
      },
    ],
    id: `${new Date().toISOString()}-esop-setup`,
    __typename: 'EsopSetup',
  }
}

export function newProjectInput(name, roundingMode, currencyCode) {
  return {
    name,
    roundingMode,
    currencyCode,
    simulateAD: true,
  }
}

export function newUserInput(user) {
  const { userId, email, firstname, lastname, newsSubscription } = user

  return userToInput({
    userId,
    email,
    firstname: firstname || '',
    lastname: lastname || '',
    newsSubscription: newsSubscription || false,
    title: '',
    avatarURL: '',
    signupTime: new Date(),
    lastLogin: new Date(),
    numberOfLogins: 1,
    business: null,
    expertType: 'BEGINNER',
    userStatus: 'FREE',
  })
}

export const equityInput = (investor, round, roundPrice, idSuffix = '') => {
  return {
    type: 'EQUITY_INVESTMENT',
    investor,
    round,
    amount: 0.0,
    pricePerShare: roundPrice,
    amountOfShares: 0,
    vestingSchedule: null,
    date: round.date,
    share: round.share,
    id: `${new Date().toISOString()}-equity-investment-${idSuffix}`,
    __typename: 'EquityInvestment',
  }
}

export const cnInput = (investor, round, idx = 0) => ({
  type: 'CN_INVESTMENT',
  investor,
  round,
  amount: 0,
  date: round.date,
  convertibleNote: {
    dateOfDeposit: round.date,
    discount: 0,
    interestRate: {
      accumulationType: Object.keys(accumulationTypes)[0],
      cumulative: false,
      interestRate: 0.0,
      retainDividends: false,
      limitInMonths: null,
      limitAsDate: null,
      id: `${new Date().toISOString()}-ir-${idx}`,
      __typename: 'InterestRate',
    },
    defaultPriceCalculationMethod: 'AUTO_PRE_MONEY_FD',
    maturityDate: null,
    preMoneyCap: null,
    preMoneyFloor: null,
    forcedPaymentOnConversion: 1,
    id: `${new Date().toISOString()}-cn-${idx}`,
    share: null,
    createdDate: new Date().toISOString(),
    __typename: 'ConvertibleNote',
  },
  id: `${new Date().toISOString()}-cn-investment-${idx}`,
  __typename: 'CnInvestment',
})

export const roundInput = (lastPostMoneyValuation, lastRoundDate) => {
  return {
    id: `${new Date().toString()}-round`,
    name: '',
    type: 'CUSTOM',
    cashIn: 0.0,
    date: lastRoundDate ? moment(lastRoundDate).add(1, 'year') : new Date(),
    preMoneyValuation: lastPostMoneyValuation || 0,
    share: null, // project.commonShare, // common share
    __typename: 'InvestmentRound',
  }
}

export const modellingRoundInput = (modellingRounds, parentSimNode) => ({
  id: `${new Date().toString()}-modelling-round`,
  name: `Scenario ${modellingRounds.length + 1}`,
  date: moment(parentSimNode.round.date).add(1, 'year').toISOString(),
  preMoneyValuation: parentSimNode.result.capTable.postMoneyValuation,
  share: parentSimNode.round.share,
  project: parentSimNode.round.project,
  __typename: 'InvestmentRound',
})

export const simNodeInput = (modellingRounds, parentSimNode, round) => ({
  id: `${new Date().toString()}-simulation-node`,
  round: {
    ...round,
  },
  children: [],
  result: {
    capTable: {
      id: `${new Date().toString()}-capTable`,
      termsheet: null,
      roundPricePerShare: 1.0,
      postMoneyValuation: 1.0,
      preMoneyValuationAsIfConverted: 1.0,
      participations: [],
      allEntries: [],
      __typename: 'CapTable',
    },
    importantPointExits: [],
    __typename: 'SimulationNodeResult',
  },
  __typename: 'SimulationNode',
})

export const modellingProjectInput = () => ({
  id: `${new Date().toString()}`,
  name: undefined,
  type: 'CALCULATION',
  createdDate: moment().toISOString(),
  rounds: [],
  __typename: 'ModellingProject',
})

export const addressInput = () => ({
  street: '',
  city: '',
  postcode: '',
  country: '',
  additional: '',
})

export const companyInput = () => ({
  name: '',
  sector: '',
  location: '',
  exist: false,
  invest: false,
  companyURL: '',
  companyLogoURL: '',
  districtCourt: '',
  registrationNumber: '',
  information: '',
  address: addressInput(),
})

export const lpInput = () => {
  return {
    agreedConversion: true,
    capType: 'NONE',
    cap: null,
    interestRate: null,
    multiple: 1,
    senior: true,
    simple: true,
    customSharePrice: null,
    id: `${new Date().toString()}-liquidationPreference`,
    __typename: 'LiquidationPreference',
  }
}

export const adInput = () => {
  return {
    compensation: Object.keys(antiDilutionCompensation)[0],
    duration: Object.keys(antiDilutionDuration)[0],
    transferType: Object.keys(antiDilutionTransferTypes)[0],
    type: Object.keys(antiDilutionTypes)[0],
    id: `${new Date().toString()}-antiDilution`,
    __typename: 'AntiDilution',
  }
}

export const shareInput = (round, shares) => {
  const name = `Share ${round.name}` // `Share ${String.fromCharCode(65 + idx)}`
  return {
    name,
    seniority:
      shares.sort((i1, i2) => i1.seniority - i2.seniority)[shares.length - 1].seniority + 1, // shares length at least 2 (Common shares), new share = highest seniority +1
    liquidationPreference: null,
    antiDilution: null,
    id: `${new Date().toString()}-share`,
    commonShare: false,
    project: round.project,
    text: '',
    votingRights: 1,
    __typename: 'Share',
    defaultPriceCalculationMethod: 'AUTO_PRE_MONEY_INC_CNS_FD',
  }
}

export const investorInput = (previousRound, idSuffix = '') => ({
  id: `${new Date().toISOString()}-investor-${idSuffix}`,
  name: '',
  email: '',
  jobTitle: null,
  salary: null,
  type: previousRound ? Object.keys(shareholderTypes)[4] : Object.keys(shareholderTypes)[0],
  createdDate: new Date().toISOString(),
  __typename: 'Investor',
})

export const employeeInput = () => ({
  id: `${new Date().toISOString()}-investor`,
  name: '',
  email: '',
  jobTitle: null,
  salary: null,
  type: 'EMPLOYEE',
  createdDate: new Date().toISOString(),
  __typename: 'Investor',
})

export const esopInput = (round, share) => ({
  round,
  date: round.date,
  id: `${new Date().toISOString()}-esopCreation`,
  type: 'ESOP_CREATION',
  shareAmount: 0,
  esop: {
    id: `${new Date().toISOString()}-esop`,
    name: null,
    notes: null,
    dilutionType: 'BY_DILUTION_OF_ALL',
    __typename: 'Esop',
    share,
  },
  valuationType: 'POST_MONEY_POOL',
  backingTable: null,
  __typename: 'EsopCreation',
})

export const defaultVestingScheduleTemplate = (startDate = undefined) => ({
  cliff: 12,
  cliffPercent: 0.3,
  accelerationOnExit: true,
  startDate,
  percentUnderlyingVesting: 1.0,
  schedule: [{ percent: 0.7, months: 36, vestingTime: 1, __typename: 'VestingPeriod' }],
  __typename: 'VestingSchedule',
})

export const vestingScheduleInput = (startDate = undefined) => ({
  startDate,
  percentUnderlyingVesting: 1.0,
  cliff: undefined,
  cliffPercent: undefined,
  accelerationOnExit: undefined,
  schedule: [],
  __typename: 'VestingSchedule',
})

export const esopGrantInput = (round, esopEvent, idx = 0) => ({
  esop: esopEvent.esop,
  round,
  receiver: {},
  vestingSchedule: esopEvent.defaultVestingSchedule || vestingScheduleInput(round.date),
  packageCalculation: null,
  id: `${new Date().toISOString()}esopGrant-${idx}`,
  __typename: 'EsopGrant',
  strikePrice: 0,
  date: round.date,
  expirationDate: null,
  shareAmount: 0,
  type: 'ESOP_GRANT',
})

export const conversionInput = (
  investor,
  round,
  convertibleNote,
  nominalAmount,
  interestAmount,
  pricePerShare,
  amountOfShares,
  id,
  idx = 0,
) => ({
  type: 'CN_CONVERSION',
  investor,
  round,
  amount: nominalAmount + interestAmount,
  interestAmount,
  nominalAmount,
  pricePerShare,
  amountOfShares,
  convertibleNote,
  date: round.date,
  share: round.share,
  id: id || `${convertibleNote.id}-conversion-${idx}`,
  __typename: 'CnConversion',
})

export const interestRateInput = () => ({
  retainDividends: true,
  accumulationType: Object.keys(accumulationTypes)[0],
  cumulative: false,
  interestRate: 0.0,
  limitInMonths: null,
  limitAsDate: null,
  id: `${new Date().toString()}-interestRate`,
  __typename: 'InterestRate',
})

export const manualSharesInput = (investor, round, project, idSuffix = '') => ({
  round,
  investor,
  share: project.commonShare,
  name: '',
  date: round.date,
  investment: 0,
  price: 0,
  amountOfShares: 0,
  isVirtualBacking: false,
  notes: '',
  type: 'MANUAL_SHARES',
  __typename: 'ManualShares',
  id: `${new Date()}-manual-share-entry-${idSuffix}`,
})

export const buyOutInput = (buyer, seller, buyingShare, sellingShare, round, idSuffix = '') => ({
  round,
  date: round.date,
  seller,
  buyer,
  buyingShare,
  sellingShare,
  amount: 0,
  pricePerShare: 0,
  vestingSchedule: null,
  type: 'BUY_OUT',
  __typename: 'BuyOut',
  id: `${new Date()}-buy-out-${idSuffix}`,
})

export const payOutInput = (idSuffix = '') => ({
  name: null,
  receivers: [],
  distributionTable: [],
  __typename: 'PayOut',
  id: `${new Date()}-buy-out-${idSuffix}`,
  seniority: undefined,
  amount: undefined,
})
